import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate } from '@angular/router';
import { Store } from '@ngrx/store';
import { isString } from 'angular';
import { catchError, map, Observable, of, switchMap, throwError, timeout } from 'rxjs';

import {
  AuthService,
  CngFacade,
  HttpService,
  isAuthenticated,
  Logger,
  RouteSelector,
  SessionFacade,
  StorageService,
  throwIfNullOrUndefined,
  waitToLoad,
} from '@contact/upgrade';

export const REDIRECT_TO_LOGIN = RouteSelector.queryParams<string>('login');

@Injectable({
  providedIn: 'root',
})
export class AuthCookieGuard implements CanActivate {
  constructor(
    private readonly logger: Logger,
    private readonly sessionFacade: SessionFacade,
    private readonly store$: Store,
    private readonly http: HttpService,
    private readonly authService: AuthService,
    private readonly storageService: StorageService
  ) {
    this.logger = this.logger.forSource('AuthCookieGuard');
  }

  canActivate(route: ActivatedRouteSnapshot): Observable<boolean> {
    // wallboard connection
    const login = REDIRECT_TO_LOGIN.resolve(route);
    if (login) {
      return this.prepareForWallboardConnection(login).pipe(switchMap(() => this.authAndWait()));
    }
    return this.authAndWait();
  }

  private authAndWait() {
    // login by session cookie
    this.sessionFacade.loginByCookie({});

    // wait user authenticated and apps initialized
    // if success redirect with params tile id
    // otherwise redirect to login page
    return this.store$.pipe(
      waitToLoad([isAuthenticated]),
      map(() => true),
      timeout(10000),
      catchError(() => {
        this.logger.error(`error during login by session cookie`);
        return of(false);
      })
    );
  }

  private prepareForWallboardConnection(login: string): Observable<string> {
    // get user uuid from login
    const getUserUuidFromLogin = (l: string) =>
      this.http
        .skipToken()
        .withUrlParams([l])
        .get(`${CngFacade.PUBLIC__TOOLS}/getUserUuidFromLogin`, { responseType: 'text' })
        .pipe(throwIfNullOrUndefined());

    // create a session for a specific agent uuid
    const wallboardConnection = (userUuid: string) =>
      this.http
        .skipToken()
        .withUrlParams([userUuid])
        .get(`${CngFacade.PROTECTED__USER}/wallboardConnection`, { responseType: 'text' })
        .pipe(throwIfNullOrUndefined());

    // map session id json and register in service
    const registerSessionId = (sessionId: string) => {
      if (!sessionId || !isString(sessionId)) {
        return throwError(() => new Error('no session id found'));
      }
      this.authService.setSessionIdInCookie(sessionId);
      this.storageService.store('wallboardAutoLogin', login, false);
      return of(sessionId);
    };

    // run
    return getUserUuidFromLogin(login).pipe(
      switchMap(uuid => wallboardConnection(uuid)),
      switchMap(sessionIdJson => registerSessionId(sessionIdJson))
    );
  }
}
